import { Component, computed, OnDestroy, OnInit, Signal, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountingApiService } from '../../../../../_services/accounting-service/accounting-api/accounting-api.service';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  AccountingConnectionStatus,
  AccountingProviderCompany,
  TenantAccountOption,
} from '../../../../../_interfaces/accounting.interface';
import { FormsModule } from '@angular/forms';
import { BoFormsModule } from '@bo/ng-forms';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { ToastService } from '../../../../../_services/toast-service/toast.service';
import { UpdatePageTitle } from '../../../../../store/app/app.actions';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import {
  SetAccountingProviderStateFromAccountingConnection,
  SetAccountingProviderStateFromSelectingTenant,
} from '../../../../../store/accounting/accounting.actions';
import { AccountingService } from '../../_services/accounting/accounting.service';
import { updateParamsObject } from '@bo/ng-utils';
import { HttpParams } from '@angular/common/http';

@Component({
  selector: 'app-tenant-account',
  imports: [NgSelectModule, FormsModule, BoFormsModule, LoaderComponent],
  templateUrl: './tenant-account.component.html',
})
export class TenantAccountComponent implements OnInit, OnDestroy {
  readonly store = inject(Store);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly accountingApiService = inject(AccountingApiService);
  readonly accountingService = inject(AccountingService);
  readonly toastService = inject(ToastService);

  protected tenantOptions: TenantAccountOption[] = [];
  protected selectedTenant: number | undefined;
  protected showError: string | undefined;
  protected queryParams = new HttpParams();

  protected accountingProvider: Signal<AccountingProviderCompany | undefined> = computed(() =>
    this.accountingService.provider(),
  );

  protected isPending: Signal<boolean> = computed(() => {
    return this.accountingService.connectionStatus() === AccountingConnectionStatus.Pending;
  });

  protected hasTenants: Signal<boolean> = computed(() => {
    return this.accountingService.connectionStatus() === AccountingConnectionStatus.HasTenants;
  });

  protected hasStatusError: Signal<boolean> = computed(() => {
    return (
      this.accountingService.connectionStatus() === AccountingConnectionStatus.Error ||
      this.accountingService.connectionStatus() === AccountingConnectionStatus.HttpError ||
      this.accountingService.connectionStatus() === AccountingConnectionStatus.AuthError
    );
  });

  protected isCompleted: Signal<boolean> = computed(() => {
    return this.accountingService.connectionStatus() === AccountingConnectionStatus.Complete;
  });

  protected newBankAccountsCreated: Signal<boolean | undefined> = computed(() => {
    return this.accountingService.providerNewBankAccountsCreated();
  });

  readonly subscriptions = new Subscription();

  constructor() {
    this.store.dispatch(new UpdatePageTitle('Integrations'));
  }

  ngOnInit(): void {
    this.store.dispatch(new SetAccountingProviderStateFromAccountingConnection());
    this.accountingApiService.getTenantAccountOptions().subscribe({
      next: (res) => {
        this.tenantOptions = res;
      },
    });
  }

  onSave() {
    if (this.selectedTenant) {
      this.subscriptions.add(
        this.accountingApiService.updateTenant(this.selectedTenant).subscribe({
          next: () => {
            // Wait for the state to update before redirecting
            this.subscriptions.add(
              this.store.dispatch(new SetAccountingProviderStateFromSelectingTenant()).subscribe({
                next: () => {
                  if (this.newBankAccountsCreated()) {
                    // The next step is to select a source account
                    this.router.navigate(['../select-source-account'], { relativeTo: this.route });
                  } else {
                    // this is a "reconnect" from a previous integration (bank accounts already exist on provider)
                    // just redirect, no need to display more details
                    this.queryParams = this.queryParams.set('reconnect', 'complete');
                    this.router.navigate(['/accounting/integrations'], {
                      queryParams: updateParamsObject(this.queryParams),
                    });
                  }
                },
              }),
            );
          },
          error: (error) => {
            if (error.non_field_errors?.length) {
              this.showError = error.error.non_field_errors[0];
              this.toastService.show(`${error.non_field_errors[0]}`, 'iconoir-xmark');
            }
          },
        }),
      );
    }
  }

  onReconnect() {
    this.accountingService.onConnect(this.accountingProvider() as AccountingProviderCompany);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected readonly AccountingProviderCompany = AccountingProviderCompany;
}
